import React, { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import SearchField from '@amzn/meridian/search-field';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import DataTable, {
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableHeader,
  DataTableCell,
} from '@amzn/meridian/data-table';
import Pagination from '@amzn/meridian/pagination';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../store';
import { selectStationAssignment } from '../../../store/slices/stationAssignmentSlice';
import { selectStationCode } from '../../../store/slices/shiftPlanSlice';
import { showError } from '../../../store/slices/errorSlice';
import { PlanStatus, ROUTES } from '../../../utils/constants';
import { getShiftRoster } from './api';
import { ShiftRosterEmployee, GetShiftRosterResponse } from './models';
import RatesRosterLoader from '../../../components/RatesRosterLoader/RatesRosterLoader';
import { MAX_RETRIES, POLLING_INTERVAL_IN_MS } from '../../rosterAndRates/constants';
import './index.scss';

const ITEMS_PER_PAGE = 15;

// Status that indicates the shift plan has been created and the roster is ready to display.
const SUCCESS_STATUSES: PlanStatus[] = [
  PlanStatus.PENDING_APPROVAL,
];

// Column sort keys
export const SORT_COLUMN_FULL_NAME = 'fullName';
export const SORT_COLUMN_CAPABILITIES = 'capabilities';

// ── Pure utility functions (exported for testability) ──

export function sortEmployees(
  employees: ShiftRosterEmployee[],
  column: string,
  direction: 'ascending' | 'descending' | 'none',
): ShiftRosterEmployee[] {
  if (direction === 'none') return employees;
  const sorted = [...employees];
  sorted.sort((a, b) => {
    let valA: string;
    let valB: string;

    if (column === SORT_COLUMN_FULL_NAME) {
      valA = a.fullName.toLowerCase();
      valB = b.fullName.toLowerCase();
    } else if (column === SORT_COLUMN_CAPABILITIES) {
      valA = a.capabilities.join(', ').toLowerCase();
      valB = b.capabilities.join(', ').toLowerCase();
    } else {
      return 0;
    }

    if (valA < valB) return direction === 'ascending' ? -1 : 1;
    if (valA > valB) return direction === 'ascending' ? 1 : -1;
    return 0;
  });
  return sorted;
}

export function filterEmployeesByName(
  employees: ShiftRosterEmployee[],
  searchQuery: string,
): ShiftRosterEmployee[] {
  if (!searchQuery.trim()) return employees;
  const query = searchQuery.toLowerCase();
  return employees.filter((e) => e.fullName.toLowerCase().includes(query));
}

// ── Component ──

const ShiftRosterPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { planId } = useAppSelector(selectStationAssignment);
  const warehouseId = useAppSelector(selectStationCode);

  const [employees, setEmployees] = useState<ShiftRosterEmployee[]>([]);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'ascending' | 'descending' | 'none'>('ascending');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  // Track polling retries across renders without recreating the fetch callback.
  const retryCountRef = useRef<number>(0);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Fetches a page of the shift roster.
   *
   * Mirrors the GetRatesAndRoster flow: while the backend reports PENDING_CREATION the
   * plan is still being built in the background, so we keep the loader up and signal the
   * caller to continue polling. Once a success status arrives we render the roster; any
   * other status (or exhausted retries) surfaces an error and stops polling.
   *
   * @returns `true` when polling should stop, `false` to keep polling.
   */
  const fetchData = useCallback(async (page: number = 1): Promise<boolean> => {
    if (!warehouseId || !planId) {
      dispatch(showError({
        title: 'Missing Parameters',
        message: 'Missing required parameters (warehouseId or planId).',
      }));
      setInitialLoading(false);
      return true;
    }

    setIsFetching(true);

    try {
      const response: GetShiftRosterResponse = await getShiftRoster(
        warehouseId,
        planId,
        page,
        ITEMS_PER_PAGE,
      );

      // Plan is still being created in the background – keep the loader up and continue polling.
      if (response.planStatus === PlanStatus.PENDING_CREATION) {
        if (retryCountRef.current >= MAX_RETRIES) {
          setInitialLoading(false);
          setIsFetching(false);
          dispatch(showError({
            title: 'Error',
            message: 'Timed out while the shift roster was being created. Please try again later.',
          }));
          return true;
        }
        retryCountRef.current += 1;
        return false; // Continue polling
      }

      // Any status other than a known success status (FAILED / REJECTED / etc.) is an error.
      if (response.planStatus && !SUCCESS_STATUSES.includes(response.planStatus)) {
        setInitialLoading(false);
        setIsFetching(false);
        dispatch(showError({
          title: 'Error',
          message: 'Failed to load shift roster data. Please try again.',
        }));
        return true;
      }

      // Success (or a legacy response without planStatus): render the roster.
      if (response.employees.length === 0) {
        dispatch(showError({
          title: 'No Data',
          message: 'Failed to load shift roster data. Please try again.',
        }));
      } else {
        setEmployees(response.employees);
        setTotalPages(response.totalPages || 1);
        setCurrentPage(response.pageNumber || page);
      }
      setInitialLoading(false);
      setIsFetching(false);
      return true;
    } catch (err: any) {
      dispatch(showError({
        title: 'Error',
        message: err.message || 'An unexpected error occurred.',
      }));
      setInitialLoading(false);
      setIsFetching(false);
      return true;
    }
  }, [warehouseId, planId, dispatch]);

  // Initial load: fetch once and, while the plan is still being created, poll until it is ready.
  // Uses a recursive setTimeout (not setInterval) so the next poll only starts after the previous
  // fetch resolves — this avoids overlapping in-flight requests. The `cancelled` flag stops any
  // in-flight continuation from scheduling a new poll after the effect is cleaned up.
  useEffect(() => {
    let cancelled = false;
    retryCountRef.current = 0;

    const poll = async () => {
      const shouldStop = await fetchData(1);
      if (cancelled || shouldStop) {
        return;
      }
      pollingTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL_IN_MS);
    };

    poll();

    return () => {
      cancelled = true;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [fetchData]);

  // Apply filtering and sorting (client-side on current page data)
  const filteredEmployees = filterEmployeesByName(employees, searchQuery);
  const sortedEmployees = sortColumn
    ? sortEmployees(filteredEmployees, sortColumn, sortDirection)
    : filteredEmployees;

  const handleSort = useCallback(({ sortColumn: col, sortDirection: dir }: { sortColumn: string; sortDirection: 'ascending' | 'descending' | 'none' }) => {
    setSortColumn(col);
    setSortDirection(dir);
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleRerun = () => {
    fetchData(currentPage);
  };

  const handlePageChange = (page: number) => {
    fetchData(page);
  };

  const handleCancel = () => {
    navigate('/');
  };

  const handleVerify = () => {
    navigate(ROUTES.SHIFT_ROTATION_ASSIGNMENTS);
  };

  // ── Loading state ──
  // Uses the same full-screen loader as GetRatesAndRoster while the plan is being created.
  if (initialLoading) {
    return (
      <Box className="shift-roster-page">
        <div data-testid="shift-roster-loader">
          <RatesRosterLoader type="roster" status="loading" />
        </div>
      </Box>
    );
  }

  return (
    <Box className="shift-roster-page" spacingInset="400">
      {/* Toolbar */}
      <Row alignmentHorizontal="justify" spacingInset="300">
        <SearchField
          value={searchQuery}
          onChange={handleSearchChange}
          onSubmit={handleSearchChange}
          placeholder="Search by associate name"
          data-testid="search-input"
        />
        <Button type="secondary" onClick={handleRerun} data-testid="rerun-button">
          Re-run rotation
        </Button>
      </Row>

      {/* Data table */}
      {isFetching && (
        <Row alignmentHorizontal="center" spacingInset="300" data-testid="shift-roster-fetching">
          <Loader size="small" />
        </Row>
      )}
      <DataTable
        layout="fixed"
        showRowDividers={true}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        data-testid="shift-roster-table"
      >
        <DataTableHead>
          <DataTableRow>
            <DataTableHeader sortColumn={SORT_COLUMN_FULL_NAME} data-testid="header-associate">Associate</DataTableHeader>
            <DataTableHeader sortColumn={SORT_COLUMN_CAPABILITIES} data-testid="header-capabilities">Trained paths</DataTableHeader>
          </DataTableRow>
        </DataTableHead>
        <DataTableBody>
          {sortedEmployees.map((employee) => (
            <DataTableRow key={employee.alias} data-testid={`row-${employee.alias}`}>
              <DataTableCell>{employee.alias}</DataTableCell>
              <DataTableCell>{employee.capabilities.join(', ')}</DataTableCell>
            </DataTableRow>
          ))}
        </DataTableBody>
      </DataTable>

      {/* Pagination */}
      <Row spacingInset="300" alignmentHorizontal="end" spacing="200" data-testid="pagination">
        <Text type="b200" color="secondary">Results per page: {ITEMS_PER_PAGE} items</Text>
        <Pagination
          numberOfPages={totalPages}
          currentPage={currentPage}
          onChange={handlePageChange}
        />
      </Row>

      {/* Footer */}
      <Row alignmentHorizontal="end" spacingInset="300" spacing="300">
        <Button type="secondary" onClick={handleCancel} data-testid="cancel-button">
          Cancel
        </Button>
        <Button type="primary" onClick={handleVerify} data-testid="verify-button">
          Verify
        </Button>
      </Row>
    </Box>
  );
};

export default ShiftRosterPage;

import './index.scss';